/**
 * Single registry of feature gate names, shared by every consumer in the
 * monorepo — backend services (`@orn/api`, `@orn/core`, `@orn/mops-api`)
 * and frontend apps (`@orn/web`, `@orn/mops-web`).
 *
 * **Why this exists:** every feature gate reference — server-side
 * `FeatureGateController.gatePasses(vc, name)` or client-side
 * `useFeatureGate` / `flags.has(...)` — must round-trip through this
 * registry instead of being passed as a bare string literal. Two reasons:
 *
 *  1. **Typo-proof.** Callers consume {@link FeatureGateName}, so
 *     `useFeatureGate('feat-assitant')` (typo) is a compile error rather
 *     than a silent always-false at runtime.
 *  2. **Discoverability.** Every gate the codebase cares about lives in
 *     this one file — operators reading the code can see the full set
 *     without grepping.
 *
 * This package is a leaf with no workspace or runtime dependencies, so
 * both Node and browser bundles can import it without dragging in
 * backend or frontend code ([ORN-850](https://linear.app/go-mantle/issue/ORN-850)
 * tracked the consolidation from per-package mirrors to this single
 * source of truth).
 *
 * **Naming conventions** for the *string values* are documented in
 * `packages/core/docs/feature-gate-naming.md`. TL;DR: lowercase +
 * hyphens, prefix with `feat-` / `kill-` / `exp-` / `ops-` / `debug-`,
 * names are public on the wire and immutable after creation.
 *
 * **Adding a gate:** append a new member here, then create the gate row
 * in mops-web's `NewFeatureGateScene` with the matching `name`. The DB
 * row is the runtime source of truth; this file is the *type*-time
 * source of truth for which names are legal in source code.
 *
 * **Removing a gate:** delete every consumer call site first, then the
 * registry entry, then the DB row. Order matters — an orphaned consumer
 * reading a deleted gate fails closed (returns `false`), which is
 * usually fine but worth doing in the right order so you never ship an
 * intermediate state where the gate is "gone" from the registry but
 * still referenced.
 */
export const FeatureGate = {
  /**
   * Gates the consumer "Bills & Subscriptions" sidebar tab. Internal
   * employees only at launch ([ORN-690](https://linear.app/go-mantle/issue/ORN-690)).
   * Eventual graduation: rule swap → percent rollout → `ALL_USERS` via
   * mops-web's New/Edit Feature Gate scenes.
   */
  Obligations: 'feat-obligations',
  /**
   * Gates the entire consumer billing surface — the `subscribe` mutation and
   * the `eligibleBillingAccounts` query: while off, no subscription can be
   * created, no checkout data is served, and no bank account can be debited.
   * This is the only thing standing between an authenticated caller and a real
   * ACH mandate, so the surface must never be registered without it. Off
   * everywhere at launch — the DB row is the runtime source of truth;
   * graduation is internal beta → percent rollout via mops-web.
   */
  BillingCheckout: 'feat-billing-checkout',
  /**
   * Gates the top-level "Mantle Assistant" nav button (and the
   * `/assistant` route) and the `/chat` WebSocket on `@orn/api`. Internal
   * employees only at launch ([ORN-692](https://linear.app/go-mantle/issue/ORN-692)).
   * Eventual graduation: rule swap → percent rollout → `ALL_USERS` via
   * mops-web's New/Edit Feature Gate scenes.
   */
  Assistant: 'feat-assistant',
  /**
   * Gates the avatar-dropdown `Debug` submenu and the `/debug/*` routes
   * (`Sentry test`, `Environment`). Internal employees only —
   * `IS_ADMIN` rule at launch ([ORN-691](https://linear.app/go-mantle/issue/ORN-691)).
   * Per the `debug-` prefix convention, this gate is expected to stay
   * pinned to internal audiences rather than graduating to `ALL_USERS`.
   */
  DebugTools: 'debug-tools',
  /**
   * Gates the `JsonDebugRow` inside transaction-family slideouts (Bill,
   * RecurringTransaction, Transaction). Replaces the old `User.isAdmin`
   * client-side check so gating is operator-controlled via mops-web rather
   * than hard-coded to the admin email list ([ORN-791](https://linear.app/go-mantle/issue/ORN-791)).
   * Per the `debug-` prefix convention, expected to stay pinned to internal
   * audiences rather than graduating to `ALL_USERS`.
   */
  DebugTransactionDetails: 'debug-transaction-details',
  /**
   * Gates the assistant chat slash-command surface. When off, a leading
   * `/issue …` is treated as ordinary chat text and flows to the LLM — the
   * orchestrator only diverts a command when this gate passes. Internal testers
   * / dogfooders only; the `/issue` flow collects internal feedback on
   * assistant responses, so it is not expected to graduate to `ALL_USERS`.
   */
  AssistantCommands: 'feat-assistant-commands',
  /**
   * Gates the two caregiver tools that enroll a loved one — `loved_one_invite`
   * and `loved_one_revoke_access`. When off they are absent from the tool
   * registry entirely, which is a stronger off state than a runtime refusal: a
   * tool that is not there cannot be described to the model, chosen by it, or
   * argued into by a user.
   *
   * Off is the default and the failure mode. Inviting sends an SMS to someone
   * who has agreed to nothing yet, so the gate is read per caller and a caller
   * that forgets to resolve it gets the tools withheld rather than granted.
   * Internal users first; expected to graduate once the enrollment funnel has
   * been watched on real households.
   */
  LovedOneInvitations: 'feat-loved-one-invitations',
  /**
   * Kill switch for the **synchronous** thread-compaction call on a
   * message-cap rollover. When ON, `bindAssistantSession` skips the
   * inline `compactThread` await on reconnect — the prior thread's
   * summary is left to the hourly async sweep instead. Use during an
   * Anthropic outage / latency spike, when blocking the bind path is
   * worse than deferring the summary.
   *
   * Default OFF (compaction runs). Fails closed to "runs" — an absent
   * or disabled gate row leaves the synchronous path active; an
   * operator enables this gate only to deliberately suspend it. The
   * async sweep has no equivalent gate: it runs cross-tenant under
   * `AsyncJobViewerContext` (no user to evaluate rules against), so it
   * is halted by disabling its EventBridge rule instead.
   */
  KillAssistantCompactionSync: 'kill-assistant-compaction-sync',
  /**
   * Internal-debug toggle for the assistant chat surface. When on, the server
   * includes each tool's name / args / output on the live `tool_started` /
   * `tool_completed` frames and the web client logs them to the browser
   * console in real time instead of rendering them — the consumer UI shows
   * only a spinner while a turn is in flight ([ORN-1342](https://linear.app/go-mantle/issue/ORN-1342)).
   * Internal employees only per the `debug-` prefix convention; not expected
   * to graduate to `ALL_USERS`.
   */
  DebugAssistant: 'debug-assistant',
  /**
   * Gates GraphQL subscriptions over the `/ws/graphql` socket
   * ([ORN-1524](https://linear.app/go-mantle/issue/ORN-1524)): the server
   * rejects ungated connections at `connection_init`, and the web client
   * skips subscribing (the lazy ws link then never connects).
   *
   * It doubles as the transport's kill switch, but it is a *drain*, not a
   * switch, and two bounds stack. `gatePasses` reads a process-local cache
   * with a 5-minute TTL held per API task, so for that long after the flip
   * some tasks still accept new sockets. Each socket then keeps its streams
   * until it turns over, because the gate is read once per connection and
   * never re-read per operation. Budget for both when using this to stop
   * something, and reach for a socket close if you need it to stop now.
   *
   * HTTP polling carries correctness throughout, which is what makes that
   * lag tolerable rather than an outage.
   */
  GraphqlSubscriptions: 'feat-graphql-subscriptions',
  /**
   * Gates the proactive weekly digest — the Sunday-morning email recapping the
   * week in a family's financial world, plus the cron sweep that fans it out.
   * Experiment / prototype: internal employees only at launch (`is_admin` rule)
   * until per-user preferences, cadence, and unsubscribe controls exist.
   * Eventual graduation via mops-web's New/Edit Feature Gate scenes.
   */
  AssistantWeeklyDigest: 'exp-assistant-weekly-digest',
  /**
   * Gates the Alerts Center — the notification bell, its unread badge, the
   * center surface, and the assistant composer's alert strip. The
   * per-transaction alert indicators (table chip, slideout row) are not gated
   * by this — they render their callout path regardless.
   *
   * Read on both sides — client surfaces via `useFeatureGate`, the
   * unread-count query server-side via `gatePasses`, so a client that skips
   * its check still reads an empty result. The `feature_gates` row must
   * therefore carry a `WEB_CLIENT` audience: under the `SERVER_ONLY` column
   * default, `gatePasses` still passes but `enabledFeatureGates` omits the
   * name and every client surface silently disappears.
   *
   * Internal employees only at launch (`is_admin` rule). Eventual
   * graduation: rule swap → percent rollout → `ALL_USERS` via mops-web's
   * New/Edit Feature Gate scenes.
   */
  AlertsCenter: 'feat-alerts-center',
} as const;

/**
 * Union of every value in {@link FeatureGate}. Pass this type to
 * `useFeatureGate` / `FeatureGateController.gatePasses` so unregistered
 * string literals fail type-check at the call site.
 */
export type FeatureGateName = (typeof FeatureGate)[keyof typeof FeatureGate];

/**
 * Dev-only resolution table for every registered feature gate. Consumed
 * by `FeatureGateController` (`@orn/core`) — both `gatePasses` and
 * `getEnabledGateNamesForViewer` — only when the server's `IS_DEV` flag
 * is `true`. In dev this map is **the sole source of truth**: the DB is
 * not consulted, so a gate doesn't need (and can't get) a `feature_gates`
 * row to evaluate true locally. In every non-dev environment (`test`,
 * `staging`, `prod`) the map is ignored entirely and gate evaluation
 * runs against `feature_gates` as before.
 *
 * **Why server-side.** Dev defaults live on the backend so there is one
 * place to manage them and the client can stay dumb — `useFeatureGate`
 * just reads what `enabledFeatureGates` returns. That keeps Storybook
 * predictable too: stories control gate state via their MSW handlers,
 * not via a parallel client-side override path.
 *
 * **Why non-`Partial`.** Every registered gate must declare its dev
 * default explicitly. Forgetting to add a new gate here is a compile
 * error rather than a silent fall-through, which used to be a common
 * tripping point ("oh man I forgot to add my gate here again"). To flip
 * a gate during testing, edit its entry below — the map is the dial.
 */
export const FEATURE_GATE_DEV_DEFAULTS: Record<FeatureGateName, boolean> = {
  [FeatureGate.Obligations]: true,
  [FeatureGate.Assistant]: true,
  [FeatureGate.DebugTools]: true,
  [FeatureGate.DebugTransactionDetails]: true,
  // On in dev defaults so manual chat sessions can exercise `/issue` end to
  // end. Production rollout is DB-side via the `feature_gates` table.
  [FeatureGate.AssistantCommands]: true,
  // On in dev so the enrollment flow can be exercised end to end locally,
  // where the SMS goes to a Twilio test number rather than a person. Every
  // deployed environment resolves this from `feature_gates` instead, and it
  // starts off there — this map is the local dial, not the rollout.
  [FeatureGate.LovedOneInvitations]: true,
  // Kill switch defaults OFF in dev so local message-cap rollovers
  // exercise the real synchronous compaction path. Flip to `true` to
  // simulate the suspended state.
  [FeatureGate.KillAssistantCompactionSync]: false,
  // On in dev so local chat sessions surface tool-call details in the
  // console — the inline tool UI is gone, so this is the dev affordance for
  // inspecting what the assistant ran. Production rollout is DB-side.
  [FeatureGate.DebugAssistant]: true,
  [FeatureGate.GraphqlSubscriptions]: true,
  // On in dev so the weekly-digest send + sweep can be exercised end to end
  // locally. Production rollout is DB-side (employee-only `is_admin` at launch).
  [FeatureGate.AssistantWeeklyDigest]: true,
  // On in dev so local work on the bell and center surfaces renders without
  // flipping the map. Production rollout is DB-side.
  [FeatureGate.AlertsCenter]: true,
  // On in dev so the subscribe path runs locally against Stripe test mode.
  // Production rollout is DB-side and starts off.
  [FeatureGate.BillingCheckout]: true,
};
